// Display formatters used across every page.
//
// `toTitleCase(name)` turns "ASHWINI RAJAN ATHALYE" into "Ashwini Rajan
// Athalye". This is the default for ANY person/client name rendered on the
// frontend — raw source data (CAMS / Karvy / KFINTECH) ships everything in
// upper-case, which looks shouty in a UI. Centralising here also lets us
// teach the helper about Indian honorifics ("Dr.", "Mr.", "Mrs."), suffixes
// ("Jr."), Mc-prefixed surnames, and apostrophes ("d'Souza") in one place.

const LOWERCASE_WORDS = new Set([
  "and", "or", "the", "of", "for", "to", "in", "on", "at", "by", "vs", "via",
]);
// Acronyms / AMC short-names / fund-types that should always render in full
// upper-case. Centralising here means we never have to chase down a stray
// "Itf"/"Etf"/"Hdfc" in some table again.
const ALWAYS_UPPER = new Set([
  // Roman numerals (suffixes — "Rajesh Kumar II")
  "ii", "iii", "iv", "v", "vi", "vii", "viii", "ix", "x",
  // Corporate suffixes
  "huf", "pvt", "ltd", "llp", "inc", "co", "&",
  // Capital-markets infrastructure
  "amc", "amfi", "nse", "bse", "sebi", "rbi", "cdsl", "nsdl",
  // Fund types / product flags
  "etf", "fof", "nfo", "ipo", "fpo", "ulip", "idcw", "ter", "aum",
  // Tax acronyms
  "ltcg", "stcg", "ltc", "stc", "tds", "tcs", "gst", "fy", "fye",
  // Investor / KYC acronyms
  "kyc", "pan", "sip", "stp", "swp", "arn", "ckyc", "iin",
  // AMC short-names (Indian mutual fund houses + custodian banks).
  // Only true acronyms here — proper names like Tata / Axis / Kotak / Mirae /
  // Nippon / Aditya stay in Title Case via the default path.
  "uti", "hdfc", "sbi", "icici", "idfc", "lic", "iti",
  "dsp", "jm", "njlf", "iifl", "bnp", "bsl", "hsbc",
  "boi", "bob", "idbi", "pgim",
]);

const titleWord = (word) => {
  if (!word) return word;
  const lower = word.toLowerCase();
  if (ALWAYS_UPPER.has(lower)) return lower.toUpperCase();
  if (LOWERCASE_WORDS.has(lower)) return lower;
  // Dot-separated acronyms (U.S., U.K., N.R.I., U.S.A.) — every dot-separated
  // segment is single-letter or short, so we keep the whole token upper.
  if (/^([a-z]\.){2,}[a-z]?\.?$/.test(lower)) {
    return word.toUpperCase();
  }
  // McDonald / O'Brien / D'Souza
  if (lower.startsWith("mc") && lower.length > 2) {
    return "Mc" + lower.charAt(2).toUpperCase() + lower.slice(3);
  }
  if (lower.length > 2 && (lower.charAt(1) === "'" || lower.charAt(1) === "’")) {
    return lower.charAt(0).toUpperCase() + lower.charAt(1) + lower.charAt(2).toUpperCase() + lower.slice(3);
  }
  // Hyphenated surnames (Smith-Jones)
  if (lower.includes("-")) {
    return lower.split("-").map(titleWord).join("-");
  }
  return lower.charAt(0).toUpperCase() + lower.slice(1);
};

export const toTitleCase = (input) => {
  if (input === null || input === undefined) return "";
  const str = String(input).trim();
  if (!str) return "";
  // Preserve all-numeric inputs (folio numbers etc.)
  if (/^[\d\s/.,-]+$/.test(str)) return str;
  return str
    .toLowerCase()
    .split(/\s+/)
    .map((w, i) => (i === 0 ? titleWord(w).replace(/^./, (c) => c.toUpperCase()) : titleWord(w)))
    .join(" ");
};

// Convenience: turn `undefined`/`null` into "—" (gentler em-dash) for table cells.
export const titleOrDash = (input) => {
  const out = toTitleCase(input);
  return out || "—";
};
